import { Book } from "@/types/book";
import { EnhancedBookCover } from "./ui/enhanced-book-cover";
import { MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import { useMostReviewedBooks } from "@/hooks/use-most-reviewed-books";
import { NetflixBookSkeleton } from "./skeletons/NetflixBookSkeleton";
import { useRef, useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { BookHoverPreview } from "./BookHoverPreview";
import { useIsMobile } from "@/hooks/use-mobile";

interface MostReviewedSectionProps {
  onBookClick: (book: Book) => void;
  allBooks: Book[];
}

export function MostReviewedSection({ onBookClick, allBooks }: MostReviewedSectionProps) {
  const { data: books, isLoading } = useMostReviewedBooks(allBooks, 9);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  // Hover preview state
  const [hoveredBook, setHoveredBook] = useState<Book | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollLeft = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -400, behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 400, behavior: 'smooth' });
    }
  }, []);

  const handleMouseEnter = useCallback((book: Book, element: HTMLDivElement) => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setAnchorRect(element.getBoundingClientRect());
      setHoveredBook(book);
    }, 1000);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const closePreview = useCallback(() => {
    setHoveredBook(null);
    setAnchorRect(null);
  }, []);

  if (isLoading) {
    return (
      <div className="relative overflow-x-hidden mb-8">
        <div className="px-5 md:px-12 xl:px-16 mb-2 md:mb-3">
          <div className="h-8 w-48 rounded bg-white/5 overflow-hidden relative">
            <div 
              className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }}
            />
          </div>
        </div>
        <div className="overflow-hidden pl-5 md:pl-12 xl:pl-16 pr-5 md:pr-12 xl:pr-16">
          <div className="flex gap-4 md:gap-6 pb-4 md:pb-6">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i} 
                className="flex-shrink-0 w-[150px] md:w-[200px] lg:w-[220px]"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <NetflixBookSkeleton />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!books || books.length === 0) {
    return null;
  }

  return (
    <div className="relative z-10 overflow-hidden mb-8">
      {/* Title with Navigation */}
      <div className="px-5 md:px-12 xl:px-16 mb-2 md:mb-3 flex items-center justify-between relative z-10">
        <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-wide uppercase">
          Most Reviewed
        </h2>
        <div className="hidden md:flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={scrollLeft}
            className="h-10 w-10 rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollRight}
            className="h-10 w-10 rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
      
      {/* Scrollable carousel */}
      <div ref={scrollContainerRef} className="overflow-x-auto pl-5 md:pl-12 xl:pl-16 pr-8 md:pr-16 xl:pr-20 hide-scrollbar relative z-10">
        <div className="flex gap-4 md:gap-6 pt-3 pb-4 md:pb-6">
          {books.map((book, index) => {
            const ranking = index + 1;
            
            return (
              <div
                key={book.id}
                className="relative flex-shrink-0 group"
                onMouseEnter={(e) => handleMouseEnter(book, e.currentTarget as HTMLDivElement)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Book Card with Ranking Number Overlay */}
                <div
                  onClick={() => { closePreview(); onBookClick(book); }}
                  className="relative z-10 cursor-pointer"
                >
                  <div className="relative w-[150px] md:w-[200px] lg:w-[220px]">
                    {/* Ranking Number - behind the book cover */}
                    <div className="absolute -left-3 md:-left-4 top-0 bottom-0 z-0 pointer-events-none select-none flex items-center">
                      <span
                        className="text-[100px] md:text-[140px] lg:text-[160px] font-black leading-none"
                        style={{
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.5) 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          textShadow: '0 0 40px rgba(168, 85, 247, 0.3)',
                          filter: 'drop-shadow(0 4px 20px rgba(168, 85, 247, 0.2))',
                        }}
                      >
                        {ranking}
                      </span>
                    </div>
                    
                    <div className="relative z-10 aspect-[2/3] rounded-[8px] border-2 border-transparent lg:group-hover:border-white/40 transition-all duration-300 overflow-hidden shadow-2xl ml-6 md:ml-8">
                      <EnhancedBookCover
                        src={book.cover_image_url || ''}
                        alt={book.title}
                        bookId={book.id}
                        cacheVersion={book.cache_version}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Review Count Badge */}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-20 ml-3 md:ml-4">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 shadow-lg">
                        <MessageSquare className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs font-bold text-white">
                          {(book.review_count || 0) >= 1000 
                            ? `${((book.review_count || 0) / 1000).toFixed(1)}k` 
                            : book.review_count || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {/* End spacer for right padding in scroll containers
              NOTE: make it a snap point so we can actually scroll into the gap. */}
          <div className="flex-none w-8 md:w-12 xl:w-16 snap-start" aria-hidden="true" />
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          .hide-scrollbar {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `
      }} />
      
      {/* Hover Preview */}
      {hoveredBook && anchorRect && (
        <BookHoverPreview
          book={hoveredBook}
          anchorRect={anchorRect}
          onClose={closePreview}
          onOpenDialog={() => { closePreview(); onBookClick(hoveredBook); }}
          onPlay={() => { closePreview(); onBookClick(hoveredBook); }}
        />
      )}
    </div>
  );
}
